import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Linkedin, Twitter, Facebook, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f4acee42-6143-402b-b6a2-a84038bb5df0/artifacts/wp0qjcw1_WhatsApp%20Image%202026-03-11%20at%2011.51.52%20AM.jpeg";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Government Projects', path: '/government-projects' },
  { name: 'Private Projects', path: '/private-projects' },
  { name: 'Media', path: '/media' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-800 bg-[#0f1419]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0f1419]/80" data-testid="header">
      {/* Top bar */}
      <div className="bg-nstu-green-800 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+919468038060" className="flex items-center gap-2 hover:text-nstu-gold-400 transition-colors" data-testid="header-phone">
              <Phone className="w-4 h-4" />
              <span>+91 94680 38060</span>
            </a>
            <a href="mailto:info@nstuindia.com" className="hidden md:flex items-center gap-2 hover:text-nstu-gold-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span>info@nstuindia.com</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-nstu-gold-400 transition-colors" aria-label="LinkedIn">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="#" className="hover:text-nstu-gold-400 transition-colors" aria-label="Twitter">
              <Twitter className="w-4 h-4" />
            </a>
            <a href="#" className="hover:text-nstu-gold-400 transition-colors" aria-label="Facebook">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="NSTU India Logo" 
              className="h-20 w-20 object-contain rounded-full shadow-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-nstu-gold-400 text-[#0f1419]'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-nstu-gold-400'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/contact">
              <Button className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="header-cta">
                Get In Touch
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-800 transition-colors text-gray-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-800 animate-fadeIn" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-md font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-nstu-gold-400 text-[#0f1419]'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="mt-4 px-4">
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 font-semibold">
                  Get In Touch
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0a0e12] text-white" data-testid="footer">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="NSTU India Logo" 
                className="h-20 w-20 object-contain rounded-full bg-white p-1"
              />
            </div>
            <h3 className="font-heading font-bold text-xl mb-2">NSTU India Pvt. Ltd.</h3>
            <p className="text-gray-400 text-sm mb-4">
              National Services Technical Unit - Delivering excellence in Government and Enterprise IT Solutions across Haryana since establishment.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-nstu-gold-400 hover:text-[#0f1419] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-nstu-gold-400 hover:text-[#0f1419] transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-nstu-gold-400 hover:text-[#0f1419] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-nstu-gold-400">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-nstu-gold-400 transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-nstu-gold-400">Our Services</h4>
            <ul className="space-y-3">
              {['e-Governance Solutions', 'Property Tax Systems', 'Bill Distribution', 'Software Development', 'IT Consulting'].map((service) => (
                <li key={service}>
                  <Link 
                    to="/services" 
                    className="text-gray-400 hover:text-nstu-gold-400 transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-nstu-gold-400">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+919468038060" className="flex items-start gap-3 text-gray-400 hover:text-nstu-gold-400 transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>+91 94680 38060</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@nstuindia.com" className="flex items-start gap-3 text-gray-400 hover:text-nstu-gold-400 transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>info@nstuindia.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>VPO Haluwas, District Bhiwani, Haryana 127021</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} NSTU India Pvt. Ltd. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-nstu-gold-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-nstu-gold-400 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#0f1419]">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
